import {
  Archive,
  ArrowRightLeft,
  BarChart3,
  Copy,
  Eye,
  GitBranch,
  Loader2,
  Plus,
  Search,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/context/AuthContext';
import {
  useArchiveCustomKpi,
  useCreateCustomKpi,
  useCreateCustomKpiVersion,
  useCustomKpiCatalog,
  useCustomKpis,
  useCustomKpiUsage,
  useMigrateCustomKpiTemplates,
  usePreviewCustomKpiMigration,
} from '@/hooks/useCustomKpis';
import { cn } from '@/lib/utils';
import type {
  CreateCustomKpiRequest,
  CustomKpiCatalogResponse,
  CustomKpiDefinition,
  CustomKpiMigrationPreviewResponse,
  CustomKpiUsageResponse,
  CustomKpiUsageTemplate,
  UpdateCustomKpiRequest,
} from '@/types/template';

import { CustomKpiEditor } from './CustomKpiEditor';

interface CustomKpiLibraryProps {
  teamId: string;
  canManage?: boolean;
}

type ActiveWorkflow =
  | { type: 'create' }
  | { type: 'version'; definition: CustomKpiDefinition }
  | { type: 'migrate'; definition: CustomKpiDefinition }
  | null;

type StatusFilter = 'all' | 'active' | 'archived';
type TemplateUsageFilter = 'all' | 'in_use' | 'pinned_to_old_version' | 'unused';

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Please review the KPI definition and try again.';
}

function DirectionBadge({ higherIsBetter }: { higherIsBetter: boolean }) {
  return (
    <span className="rounded-full bg-surface-subtle px-2 py-0.5 text-xs font-medium text-secondary">
      {higherIsBetter ? 'Higher better' : 'Lower better'}
    </span>
  );
}

function StatusBadge({ status }: { status: CustomKpiDefinition['status'] }) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs font-medium',
        status === 'active' ? 'bg-success/10 text-success' : 'bg-surface-subtle text-muted'
      )}
    >
      {status}
    </span>
  );
}

function getPlatformBadge(
  definition: CustomKpiDefinition,
  catalog: CustomKpiCatalogResponse | undefined
): string {
  const metrics = (catalog?.baseMetrics ?? []).filter((metric) =>
    definition.baseMetricIds.includes(metric.id)
  );
  if (metrics.length === 0) return 'Platform-ready';

  const hasMetaMetric = metrics.some((metric) => {
    const metaStatus = metric.platformAvailability.meta;
    return metaStatus === 'available' || metaStatus === 'supported';
  });
  const hasFuturePlatform = metrics.some((metric) =>
    Object.entries(metric.platformAvailability).some(
      ([platform, availability]) => platform !== 'meta' && availability !== 'unavailable'
    )
  );

  if (hasMetaMetric && !hasFuturePlatform) return 'Meta only';
  if (hasMetaMetric) return 'Meta';
  return 'Platform-ready';
}

function formatUnitDisplay(
  definition: CustomKpiDefinition,
  catalog: CustomKpiCatalogResponse | undefined
): string {
  const unit = catalog?.units.find((option) => option.id === definition.unit);
  if (!unit) return definition.unit;
  return `${unit.label} (${unit.displayFormat || unit.symbol})`;
}

function getTemplatePinSummary(
  usage: CustomKpiUsageResponse | null | undefined,
  latestVersion: number
): string {
  const pinnedTemplates = (usage?.templates ?? []).filter(
    (template) => template.currentVersion !== null
  );
  const pinnedCount = pinnedTemplates.length;

  if (pinnedCount === 0) return 'No templates pinned';

  const olderPinnedCount = pinnedTemplates.filter(
    (template) => template.currentVersion !== null && template.currentVersion < latestVersion
  ).length;

  if (olderPinnedCount === 1) return '1 template pinned to older version';
  if (olderPinnedCount > 1) return `${olderPinnedCount} templates pinned to older versions`;

  if (pinnedTemplates.every((template) => template.currentVersion === latestVersion)) {
    return 'All templates on latest';
  }

  return pinnedCount === 1 ? '1 template pinned' : `${pinnedCount} templates pinned`;
}

function getMigrationDisabledReason(
  definition: CustomKpiDefinition,
  isAdmin: boolean,
  migrationSourceVersions: number[]
): string | null {
  if (!isAdmin) return 'Members cannot migrate templates';
  if (definition.status === 'archived') return 'Archived KPIs cannot be migrated';
  if (migrationSourceVersions.length > 0) return null;
  if (definition.version > 1) return 'All templates on latest';
  return 'No older template pins';
}

function getEligibleTemplates(
  usage: CustomKpiUsageResponse | null,
  fromVersion: number
): CustomKpiUsageTemplate[] {
  return (usage?.templates ?? []).filter((template) => template.currentVersion === fromVersion);
}

function getMigrationSourceVersions(
  usage: CustomKpiUsageResponse | null,
  targetVersion: number
): number[] {
  const sourceVersions = new Set<number>();

  for (const template of usage?.templates ?? []) {
    if (template.currentVersion !== null && template.currentVersion < targetVersion) {
      sourceVersions.add(template.currentVersion);
    }
  }

  return Array.from(sourceVersions).sort((a, b) => a - b);
}

interface CustomKpiRowProps {
  definition: CustomKpiDefinition;
  teamId: string;
  catalog?: CustomKpiCatalogResponse;
  isAdmin: boolean;
  isArchivePending: boolean;
  onCreateVersion: (definition: CustomKpiDefinition) => void;
  onArchive: (definition: CustomKpiDefinition) => void;
  onOpenMigration: (
    definition: CustomKpiDefinition,
    usage: CustomKpiUsageResponse | null
  ) => void;
  onCopyFormula: (formula: string) => void;
}

function CustomKpiRow({
  definition,
  teamId,
  catalog,
  isAdmin,
  isArchivePending,
  onCreateVersion,
  onArchive,
  onOpenMigration,
  onCopyFormula,
}: CustomKpiRowProps) {
  const { data: usage, isLoading: isUsageLoading } = useCustomKpiUsage(
    teamId,
    definition.customKpiId
  );
  const migrationSourceVersions = getMigrationSourceVersions(usage ?? null, definition.version);
  const migrationDisabledReason = getMigrationDisabledReason(
    definition,
    isAdmin,
    migrationSourceVersions
  );
  const canMigrate = migrationDisabledReason === null;
  const platformBadge = getPlatformBadge(definition, catalog);
  const unitDisplay = formatUnitDisplay(definition, catalog);
  const templatePinSummary = isUsageLoading
    ? 'Loading...'
    : getTemplatePinSummary(usage, definition.version);

  return (
    <article className="rounded-xl border border-border-subtle bg-surface p-4 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-brand/10 px-2.5 py-1 text-sm font-bold text-brand">
                  {definition.shortLabel}
                </span>
                <h4 className="min-w-0 text-base font-bold text-primary">{definition.name}</h4>
              </div>
              {definition.description && (
                <p className="mt-1 line-clamp-2 text-sm text-secondary">{definition.description}</p>
              )}
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <StatusBadge status={definition.status} />
              <span className="rounded-full bg-surface-subtle px-2 py-0.5 text-xs font-medium text-secondary">
                Latest v{definition.version}
              </span>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-medium',
                  templatePinSummary.includes('older')
                    ? 'bg-warning/10 text-warning'
                    : 'bg-surface-subtle text-secondary'
                )}
              >
                {templatePinSummary}
              </span>
            </div>
          </div>

          <div className="mt-4 grid gap-0 overflow-hidden rounded-lg border border-border-subtle text-sm sm:grid-cols-3">
            <div className="border-b border-border-subtle p-3 sm:border-b-0 sm:border-r">
              <div className="text-xs font-semibold text-muted">Unit</div>
              <div className="mt-1 font-medium text-primary">{unitDisplay}</div>
            </div>
            <div className="border-b border-border-subtle p-3 sm:border-b-0 sm:border-r">
              <div className="text-xs font-semibold text-muted">Direction</div>
              <div className="mt-1">
                <DirectionBadge higherIsBetter={definition.higherIsBetter} />
              </div>
            </div>
            <div className="p-3">
              <div className="text-xs font-semibold text-muted">Platform</div>
              <div className="mt-1 font-medium text-primary">{platformBadge}</div>
            </div>
          </div>

          <div className="mt-4 min-w-0 rounded-lg border border-border-subtle bg-surface-subtle/70 p-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="text-xs font-semibold text-muted">Formula</div>
              <button
                type="button"
                onClick={() => onCopyFormula(definition.formula)}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-surface hover:text-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/12"
                aria-label={`Copy formula for ${definition.shortLabel}`}
                title="Copy formula"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
            <code
              className="block max-w-full overflow-x-auto font-mono text-xs text-secondary"
              title={definition.formula}
            >
              {definition.formula}
            </code>
          </div>
        </div>

        {isAdmin && definition.status === 'active' && (
          <div className="grid w-full shrink-0 grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center xl:max-w-[390px] xl:justify-end">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onCreateVersion(definition)}
              title="Open Custom KPI"
              aria-label={`Open ${definition.shortLabel}`}
              className="min-w-0 px-3"
              leftIcon={<Eye className="h-4 w-4" />}
            >
              Open
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onCreateVersion(definition)}
              title="Create version"
              aria-label={`Create version for ${definition.shortLabel}`}
              className="min-w-0 px-3"
              leftIcon={<GitBranch className="h-4 w-4" />}
            >
              Create version
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onOpenMigration(definition, usage ?? null)}
              disabled={!canMigrate}
              title={migrationDisabledReason ?? 'Migrate templates'}
              aria-label={`Migrate templates for ${definition.shortLabel}`}
              className="min-w-0 px-3"
              leftIcon={<ArrowRightLeft className="h-4 w-4" />}
            >
              Migrate templates
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onArchive(definition)}
              loading={isArchivePending}
              title="Archive Custom KPI"
              aria-label={`Archive ${definition.shortLabel}`}
              className="min-w-0 px-3 text-danger hover:text-danger sm:w-9 sm:px-0"
              leftIcon={<Archive className="h-4 w-4" />}
            >
              <span className="sm:sr-only">Archive</span>
            </Button>
          </div>
        )}
      </div>
    </article>
  );
}

export function CustomKpiLibrary({ teamId, canManage }: CustomKpiLibraryProps) {
  const { currentTeam } = useAuth();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [templateUsageFilter, setTemplateUsageFilter] = useState<TemplateUsageFilter>('all');
  const [activeWorkflow, setActiveWorkflow] = useState<ActiveWorkflow>(null);
  const [migrationUsage, setMigrationUsage] = useState<CustomKpiUsageResponse | null>(null);
  const [migrationFromVersion, setMigrationFromVersion] = useState<number | null>(null);
  const [migrationPreview, setMigrationPreview] =
    useState<CustomKpiMigrationPreviewResponse | null>(null);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [migrationError, setMigrationError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const previewRequestRef = useRef(0);
  const toast = useToast();

  const { data: definitions = [], isLoading } = useCustomKpis(teamId, includeArchived);
  const {
    data: catalog,
    isLoading: isCatalogLoading,
    error: catalogError,
  } = useCustomKpiCatalog(teamId);
  const createCustomKpi = useCreateCustomKpi(teamId);
  const createCustomKpiVersion = useCreateCustomKpiVersion(teamId);
  const archiveCustomKpi = useArchiveCustomKpi(teamId);
  const previewMigration = usePreviewCustomKpiMigration(teamId);
  const migrateTemplates = useMigrateCustomKpiTemplates(teamId);

  const isAdmin = canManage ?? (currentTeam?.teamId === teamId && currentTeam.role === 'admin');
  const editingDefinition =
    activeWorkflow?.type === 'version' ? activeWorkflow.definition : null;
  const migrationDefinition =
    activeWorkflow?.type === 'migrate' ? activeWorkflow.definition : null;

  const { activeDefinitions, archivedDefinitions } = useMemo(() => {
    return {
      activeDefinitions: definitions.filter((definition) => definition.status === 'active'),
      archivedDefinitions: definitions.filter((definition) => definition.status === 'archived'),
    };
  }, [definitions]);

  const platformOptions = useMemo(() => {
    const options = new Set<string>();
    for (const definition of definitions) {
      options.add(getPlatformBadge(definition, catalog));
    }
    return Array.from(options).sort();
  }, [catalog, definitions]);

  const visibleDefinitions = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    return definitions.filter((definition) => {
      if (!includeArchived && definition.status === 'archived') return false;
      if (statusFilter !== 'all' && definition.status !== statusFilter) return false;
      if (platformFilter !== 'all' && getPlatformBadge(definition, catalog) !== platformFilter) {
        return false;
      }
      if (!normalizedSearch) return true;
      return [
        definition.name,
        definition.shortLabel,
        definition.description ?? '',
        definition.formula,
        getPlatformBadge(definition, catalog),
      ]
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [
    catalog,
    definitions,
    includeArchived,
    platformFilter,
    searchQuery,
    statusFilter,
  ]);

  const closeEditor = () => {
    setActiveWorkflow(null);
    setMutationError(null);
  };

  const closeMigrationData = () => {
    previewRequestRef.current += 1;
    setMigrationUsage(null);
    setMigrationFromVersion(null);
    setMigrationPreview(null);
    setSelectedTemplateIds([]);
    setMigrationError(null);
  };

  const closeMigration = () => {
    setActiveWorkflow(null);
    closeMigrationData();
  };

  const handleCreate = async (data: CreateCustomKpiRequest | UpdateCustomKpiRequest) => {
    setMutationError(null);
    try {
      await createCustomKpi.mutateAsync(data as CreateCustomKpiRequest);
      toast.success('Custom KPI created', 'The KPI is available for templates.');
      closeEditor();
    } catch (error) {
      setMutationError(getErrorMessage(error));
    }
  };

  const handleUpdate = async (data: CreateCustomKpiRequest | UpdateCustomKpiRequest) => {
    if (activeWorkflow?.type !== 'version') return;
    setMutationError(null);
    try {
      await createCustomKpiVersion.mutateAsync({
        customKpiId: activeWorkflow.definition.customKpiId,
        data: data as UpdateCustomKpiRequest,
      });
      toast.success(
        'Version created',
        'Existing templates were not migrated. Use Migrate templates to update selected pins.'
      );
      closeEditor();
    } catch (error) {
      setMutationError(getErrorMessage(error));
    }
  };

  const previewMigrationSource = async (
    definition: CustomKpiDefinition,
    usage: CustomKpiUsageResponse | null,
    fromVersion: number
  ) => {
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    const eligibleTemplates = getEligibleTemplates(usage, fromVersion);

    setMigrationPreview(null);
    setSelectedTemplateIds([]);
    setMigrationError(null);

    if (eligibleTemplates.length === 0) {
      return;
    }

    try {
      const preview = await previewMigration.mutateAsync({
        customKpiId: definition.customKpiId,
        data: {
          fromVersion,
          toVersion: definition.version,
          templateIds: eligibleTemplates.map((template) => template.templateId),
        },
      });
      if (previewRequestRef.current !== requestId) {
        return;
      }
      setMigrationPreview(preview);
    } catch (error) {
      if (previewRequestRef.current !== requestId) {
        return;
      }
      setMigrationError(getErrorMessage(error));
    }
  };

  const openMigration = async (
    definition: CustomKpiDefinition,
    usage: CustomKpiUsageResponse | null
  ) => {
    const sourceVersions = getMigrationSourceVersions(usage, definition.version);
    const defaultFromVersion = sourceVersions[0] ?? null;

    setActiveWorkflow({ type: 'migrate', definition });
    setMutationError(null);
    setMigrationUsage(usage);
    setMigrationFromVersion(defaultFromVersion);
    setMigrationPreview(null);
    setSelectedTemplateIds([]);
    setMigrationError(null);

    if (defaultFromVersion === null) {
      previewRequestRef.current += 1;
      return;
    }

    await previewMigrationSource(definition, usage, defaultFromVersion);
  };

  const changeMigrationSourceVersion = async (fromVersion: number) => {
    if (!migrationDefinition) return;
    setMigrationFromVersion(fromVersion);
    await previewMigrationSource(migrationDefinition, migrationUsage, fromVersion);
  };

  const toggleTemplateSelection = (templateId: string) => {
    setSelectedTemplateIds((current) =>
      current.includes(templateId)
        ? current.filter((currentId) => currentId !== templateId)
        : [...current, templateId]
    );
  };

  const applyMigration = async () => {
    if (!migrationDefinition || migrationFromVersion === null || selectedTemplateIds.length === 0) {
      return;
    }

    setMigrationError(null);
    try {
      await migrateTemplates.mutateAsync({
        customKpiId: migrationDefinition.customKpiId,
        data: {
          fromVersion: migrationFromVersion,
          toVersion: migrationDefinition.version,
          templateIds: selectedTemplateIds,
        },
      });
      toast.success(
        'Templates migrated',
        `${selectedTemplateIds.length} template${selectedTemplateIds.length === 1 ? '' : 's'} now use v${migrationDefinition.version}.`
      );
      closeMigration();
    } catch (error) {
      setMigrationError(getErrorMessage(error));
    }
  };

  const handleArchive = async (definition: CustomKpiDefinition) => {
    if (
      !confirm(
        `Archive ${definition.shortLabel}? New templates will not be able to select it. Existing template pins and verdict history remain unchanged.`
      )
    ) {
      return;
    }
    try {
      await archiveCustomKpi.mutateAsync(definition.customKpiId);
      toast.success('Custom KPI archived', `${definition.shortLabel} is hidden from new templates.`);
    } catch (error) {
      toast.error('Archive failed', getErrorMessage(error));
    }
  };

  const handleCopyFormula = async (formula: string) => {
    try {
      await navigator.clipboard?.writeText(formula);
      toast.success('Formula copied', formula);
    } catch {
      toast.warning('Copy unavailable', formula);
    }
  };

  const renderRows = (rows: CustomKpiDefinition[], emptyLabel: string) => {
    if (rows.length === 0) {
      const [emptyHeading, emptyBody] = emptyLabel.split('\n');
      return (
        <div className="rounded-lg border border-border-subtle bg-surface-subtle/40 px-4 py-8 text-center">
          <div className="text-sm font-semibold text-primary">{emptyHeading}</div>
          {emptyBody && <p className="mt-1 text-sm text-secondary">{emptyBody}</p>}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {rows.map((definition) => (
          <CustomKpiRow
            key={`${definition.customKpiId}-${definition.version}`}
            definition={definition}
            teamId={teamId}
            catalog={catalog}
            isAdmin={isAdmin}
            isArchivePending={archiveCustomKpi.isPending}
            onCreateVersion={(selectedDefinition) => {
              setActiveWorkflow({ type: 'version', definition: selectedDefinition });
              setMutationError(null);
              closeMigrationData();
            }}
            onArchive={handleArchive}
            onOpenMigration={openMigration}
            onCopyFormula={handleCopyFormula}
          />
        ))}
      </div>
    );
  };

  const isSaving = createCustomKpi.isPending || createCustomKpiVersion.isPending;
  const migrationSourceVersions = migrationDefinition
    ? getMigrationSourceVersions(migrationUsage, migrationDefinition.version)
    : [];
  const activeMigrationTemplates =
    migrationFromVersion === null ? [] : getEligibleTemplates(migrationUsage, migrationFromVersion);
  const selectedMigrationTemplates = activeMigrationTemplates.filter((template) =>
    selectedTemplateIds.includes(template.templateId)
  );
  const migrationRows =
    migrationPreview?.templates ??
    activeMigrationTemplates.map((template) => ({
      ...template,
      targetVersion: migrationDefinition?.version ?? 1,
      selected: false,
    }));

  if ((activeWorkflow?.type === 'create' || activeWorkflow?.type === 'version') && isAdmin) {
    return (
      <div className="space-y-5">
        <div className="rounded-[24px] border border-border-subtle bg-surface p-4 shadow-sm sm:p-5">
          {isCatalogLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-brand" />
            </div>
          ) : catalogError || !catalog ? (
            <div
              className="rounded-lg border border-danger/20 bg-danger/10 px-3 py-2 text-sm text-danger"
              role="alert"
            >
              KPI catalog unavailable. Refresh the page or try again before creating or migrating KPIs.
            </div>
          ) : (
            <CustomKpiEditor
              key={
                editingDefinition
                  ? `version-${editingDefinition.customKpiId}-${editingDefinition.version}`
                  : 'create-new-custom-kpi'
              }
              initialValue={editingDefinition ?? undefined}
              catalog={catalog}
              nextVersion={editingDefinition ? editingDefinition.version + 1 : undefined}
              onSubmit={activeWorkflow.type === 'version' ? handleUpdate : handleCreate}
              onCancel={closeEditor}
              isSaving={isSaving || isCatalogLoading}
              apiError={mutationError}
            />
          )}
        </div>

        <Card className="rounded-[20px] p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-sm font-bold text-primary">Active definitions</h3>
              <p className="mt-1 text-sm text-secondary">Manage and review your active KPIs.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {activeWorkflow.type === 'version' && (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setActiveWorkflow({ type: 'create' });
                    setMutationError(null);
                    closeMigrationData();
                  }}
                  leftIcon={<Plus className="h-4 w-4" />}
                >
                  Create KPI
                </Button>
              )}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setIncludeArchived((current) => !current)}
                leftIcon={<Archive className="h-4 w-4" />}
              >
                {includeArchived ? 'Hide archived' : 'Show archived'}
              </Button>
            </div>
          </div>
          <div className="mt-4">
            {renderRows(
              activeDefinitions,
              'No Custom KPIs yet\nCreate a governed KPI definition to make it available in templates and verdict rules.'
            )}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <Card className="rounded-[24px] p-5 sm:p-6">
      <div className="space-y-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand">
              <BarChart3 className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-2xl font-bold text-primary">Custom KPIs</h2>
              <p className="mt-1 text-sm text-secondary">
                Manage your KPI definitions, versions, and template usage.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-2.5 py-1 text-xs font-semibold text-success">
                  <span className="h-1.5 w-1.5 rounded-full bg-success" />
                  {activeDefinitions.length} active
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-subtle px-2.5 py-1 text-xs font-semibold text-secondary">
                  <span className="h-1.5 w-1.5 rounded-full bg-muted" />
                  {archivedDefinitions.length} archived
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-warning/10 px-2.5 py-1 text-xs font-semibold text-warning">
                  <span className="h-1.5 w-1.5 rounded-full bg-warning" />
                  Review template pins
                </span>
              </div>
            </div>
          </div>
          {isAdmin && (
            <Button
              type="button"
              className="w-full sm:w-auto xl:self-start"
              onClick={() => {
                setActiveWorkflow({ type: 'create' });
                setMutationError(null);
                closeMigrationData();
              }}
              leftIcon={<Plus className="h-4 w-4" />}
              disabled={isCatalogLoading || !!catalogError}
            >
              Create KPI
            </Button>
          )}
        </div>

        {!isAdmin && (
          <div className="rounded-lg border border-border-subtle bg-surface-subtle/50 px-4 py-3 text-sm text-secondary">
            Members can view Custom KPIs. Admins manage versions, migrations, and archived status.
          </div>
        )}

        {catalogError && (
          <div
            className="rounded-lg border border-danger/20 bg-danger/10 px-3 py-2 text-sm text-danger"
            role="alert"
          >
            KPI catalog unavailable. Refresh the page or try again before creating or migrating KPIs.
          </div>
        )}

        {activeWorkflow?.type === 'migrate' && migrationDefinition && isAdmin && (
          <div className="rounded-lg border border-warning/30 bg-warning/10 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-base font-semibold text-primary">
                  Migrate templates for {migrationDefinition.shortLabel}
                </h3>
                <p className="mt-1 text-sm text-secondary">
                  Migrate only the templates you want to move to v{migrationDefinition.version}. Existing analyses and verdict history are unchanged.
                </p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={closeMigration}>
                Cancel
              </Button>
            </div>

            {migrationError && (
              <div
                className="mt-3 rounded-lg border border-danger/20 bg-danger/10 px-3 py-2 text-sm text-danger"
                role="alert"
              >
                {migrationError}
              </div>
            )}

            {migrationFromVersion !== null && (
              <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-secondary">
                <span className="font-semibold text-primary">
                  From v{migrationFromVersion}
                </span>
                <span>to</span>
                <span className="font-semibold text-primary">
                  Latest v{migrationDefinition.version}
                </span>
                {migrationSourceVersions.length > 1 && (
                  <div className="ml-0 flex flex-wrap items-center gap-1 sm:ml-3">
                    {migrationSourceVersions.map((sourceVersion) => (
                      <Button
                        key={sourceVersion}
                        type="button"
                        variant={sourceVersion === migrationFromVersion ? 'primary' : 'ghost'}
                        size="sm"
                        onClick={() => changeMigrationSourceVersion(sourceVersion)}
                        disabled={sourceVersion === migrationFromVersion}
                      >
                        From v{sourceVersion}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 rounded-lg border border-border-subtle bg-surface">
              {migrationRows.length > 0 ? (
                migrationRows.map((template) => {
                  const isValid = template.currentVersion === migrationFromVersion;
                  const isSelected = selectedTemplateIds.includes(template.templateId);
                  const disabledReason = isValid
                    ? null
                    : `Only templates pinned to v${migrationFromVersion ?? 'the selected version'} can be migrated in this batch.`;
                  return (
                    <label
                      key={template.templateId}
                      className={cn(
                        'flex flex-col gap-3 border-b border-border-subtle px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between',
                        !isValid && 'bg-surface-subtle/45 text-muted'
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 shrink-0 rounded border-border-subtle text-brand focus:ring-brand"
                          checked={isSelected}
                          disabled={!isValid}
                          onChange={() => toggleTemplateSelection(template.templateId)}
                          aria-label={`Migrate ${template.name} from v${template.currentVersion ?? 'unknown'} to v${migrationDefinition.version}`}
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-primary">
                            {template.name}
                          </span>
                          <span className="block text-xs text-secondary">
                            {template.usesInRules ? 'Used in verdict rules' : 'Primary KPI only'}
                          </span>
                          {disabledReason && (
                            <span className="mt-1 block text-xs text-muted">{disabledReason}</span>
                          )}
                        </span>
                      </span>
                      <span className="flex flex-wrap gap-2 text-sm text-secondary">
                        <span className="rounded-full bg-surface-subtle px-2 py-0.5">
                          Current v{template.currentVersion ?? 'unknown'}
                        </span>
                        <span className="rounded-full bg-brand/10 px-2 py-0.5 text-brand">
                          Target v{migrationDefinition.version}
                        </span>
                      </span>
                    </label>
                  );
                })
              ) : (
                <div className="px-4 py-6 text-sm text-secondary">
                  {migrationFromVersion === null
                    ? 'No templates are pinned to an older version.'
                    : `No templates are pinned to v${migrationFromVersion}.`}
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center justify-end">
              <Button
                type="button"
                onClick={applyMigration}
                disabled={selectedMigrationTemplates.length === 0 || migrateTemplates.isPending}
                loading={migrateTemplates.isPending}
              >
                Migrate selected templates
              </Button>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-border-subtle bg-surface-subtle/45 p-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-[minmax(220px,1fr)_repeat(3,minmax(0,180px))_minmax(0,160px)]">
            <label className="block min-w-0 md:col-span-2 xl:col-span-1">
              <span className="sr-only">Search KPIs</span>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search KPIs..."
                  className="h-10 w-full min-w-0 rounded-md border border-border-subtle bg-surface pl-9 pr-3 text-sm text-primary placeholder:text-muted focus-visible:border-brand/55 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/12"
                />
              </div>
            </label>
            <label className="block min-w-0">
              <span className="sr-only">Status filter</span>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
                className="h-10 w-full min-w-0 rounded-md border border-border-subtle bg-surface px-3 text-sm text-primary focus-visible:border-brand/55 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/12"
              >
                <option value="all">Status: All</option>
                <option value="active">Status: Active</option>
                <option value="archived">Status: Archived</option>
              </select>
            </label>
            <label className="block min-w-0">
              <span className="sr-only">Platform filter</span>
              <select
                value={platformFilter}
                onChange={(event) => setPlatformFilter(event.target.value)}
                className="h-10 w-full min-w-0 rounded-md border border-border-subtle bg-surface px-3 text-sm text-primary focus-visible:border-brand/55 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/12"
              >
                <option value="all">Platform: All</option>
                {platformOptions.map((platform) => (
                  <option key={platform} value={platform}>
                    Platform: {platform}
                  </option>
                ))}
              </select>
            </label>
            <label className="block min-w-0">
              <span className="sr-only">Template usage filter</span>
              <select
                value={templateUsageFilter}
                onChange={(event) =>
                  setTemplateUsageFilter(event.target.value as TemplateUsageFilter)
                }
                className="h-10 w-full min-w-0 rounded-md border border-border-subtle bg-surface px-3 text-sm text-primary focus-visible:border-brand/55 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/12"
              >
                <option value="all">Template usage: All</option>
                <option value="in_use">Template usage: In use</option>
                <option value="pinned_to_old_version">Template usage: Needs migration</option>
                <option value="unused">Template usage: Unused</option>
              </select>
            </label>
            <label className="inline-flex h-10 min-w-0 items-center gap-2 rounded-md border border-border-subtle bg-surface px-3 text-sm text-secondary">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(event) => setIncludeArchived(event.target.checked)}
                className="h-4 w-4 rounded border-border-subtle text-brand focus:ring-brand"
              />
              <span className="min-w-0 truncate">Show archived</span>
            </label>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-brand" />
          </div>
        ) : (
          <div className="space-y-5">
            {renderRows(
              visibleDefinitions,
              definitions.length === 0
                ? 'No Custom KPIs yet\nCreate a governed KPI definition to make it available in templates and verdict rules.'
                : 'No KPIs match your filters\nTry adjusting your search, status, platform, or template usage filters.'
            )}
            {includeArchived && archivedDefinitions.length === 0 && (
              <div className="rounded-lg border border-border-subtle bg-surface-subtle/40 px-4 py-8 text-center">
                <div className="text-sm font-semibold text-primary">No archived Custom KPIs</div>
              </div>
            )}
            <div className="flex flex-col gap-3 border-t border-border-subtle pt-4 text-sm text-secondary sm:flex-row sm:items-center sm:justify-between">
              <span>
                Showing {visibleDefinitions.length === 0 ? 0 : 1}-
                {visibleDefinitions.length} of {visibleDefinitions.length} KPIs
              </span>
              <div className="flex items-center gap-2">
                <Button type="button" variant="secondary" size="sm" disabled>
                  ‹
                </Button>
                <span className="inline-flex h-9 min-w-9 items-center justify-center rounded-md bg-brand/10 px-3 text-sm font-semibold text-brand">
                  1
                </span>
                <Button type="button" variant="secondary" size="sm" disabled>
                  ›
                </Button>
                <select
                  className="h-9 rounded-md border border-border-subtle bg-surface px-3 text-sm text-primary focus-visible:border-brand/55 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/12"
                  defaultValue="20"
                  aria-label="KPIs per page"
                >
                  <option value="20">20 per page</option>
                  <option value="50">50 per page</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
